import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useIntl, defineMessages } from 'react-intl';
import { Search } from 'semantic-ui-react';
import _ from 'lodash';
import { Map } from '../../';
import { Input, Button, Message } from 'semantic-ui-react';
import { FormFieldWrapper } from '@plone/volto/components';
import config from '@plone/volto/registry';
import { faTradeFederation } from '@fortawesome/free-brands-svg-icons';

const messages = defineMessages({
  searchPlaceholder: {
    id: 'leaflet_widget_search_placeholder',
    defaultMessage: 'Type an address',
  },
  searchButton: {
    id: 'leaflet_widget_search_button',
    defaultMessage: 'Search',
  },
  errorMessage: {
    id: 'leaflet_widget_error',
    defaultMessage: 'Location not found. Please try another address.',
  },
  searchingMessage: {
    id: 'leaflet_widget_searching',
    defaultMessage: 'Searching...',
  },
});

const LeafletWidget = (props) => {
  const intl = useIntl();
  const {
    id,
    value,
    onChange,
    onBlur,
  } = props;
  const inputId = `field-${id}`;
  const searchInputId = `field-search-${id}`;

  const getInitialPosition = function () {
    if (!value) {
      return config.settings.mapCenter;
    }
    if (!value.includes(';')) {
      return config.settings.mapCenter;
    }
    return value.split(';').map(parseFloat);
  };

  const [position, setPosition] = useState(getInitialPosition);
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const onMarkerMove = (value) => {
    const newValue = (value?.lat && value?.lng) ? value?.lat + ';' + value?.lng : undefined;
    onChange(id, newValue === '' ? undefined : newValue);
  };

  const handleInputChange = ({ target }) => {
    const coords = target.value.split(';').map(parseFloat);
    if (coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
      setPosition(coords);
      onChange(id, target.value);
    }
  };

  const handleSearchChange = ({ target }) => {
    setSearchValue(target.value);
    setErrorMessage(null);
  };

  const geolocate = async () => {
    if (!searchValue.trim()) {
      setErrorMessage(intl.formatMessage(messages.errorMessage));
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          searchValue
        )}&format=json`
      );

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();

      if (data.length > 0) {
        const result = data[0];
        const newCoords = [parseFloat(result.lat), parseFloat(result.lon)];
        
        // Update position state
        setPosition(newCoords);
        
        // Update the coordinate input value
        const coordString = newCoords[0] + ';' + newCoords[1];
        onChange(id, coordString);
        
        // Clear search input and error
        setSearchValue('');
        setErrorMessage(null);
      } else {
        setErrorMessage(intl.formatMessage(messages.errorMessage));
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      setErrorMessage(intl.formatMessage(messages.errorMessage));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearCoordinates = () => {
    onChange(id, undefined);
    setPosition(config.settings.mapCenter);
    setSearchValue('');
    setErrorMessage(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      geolocate();
    }
  };

  return (
    <>
      <FormFieldWrapper {...props} className="leaflet-widget">
        <div className="leaflet-input">
          <Input
            id={inputId}
            name={id}
            type="text"
            placeholder="latitude;longitude"
            onChange={handleInputChange}
            onBlur={({ target }) =>
              onBlur(id, target.value === '' ? undefined : target.value)
            }
            value={value || ''}
            readOnly={false}
          />
        </div>
      </FormFieldWrapper>

      <div className="p-4">

        <div className="leaflet-search-container">
          <div>
            <Input
              id={searchInputId}
              placeholder={intl.formatMessage(messages.searchPlaceholder)}
              value={searchValue}
              onChange={handleSearchChange}
              onKeyPress={handleKeyPress}
            />
            <Button
              primary
              onClick={geolocate}
              disabled={isLoading || !searchValue.trim()}
              loading={isLoading}
            >
              {intl.formatMessage(messages.searchButton)}
            </Button>
          </div>

          {errorMessage && (
            <Message negative style={{ marginBottom: '8px' }}>
              {errorMessage}
            </Message>
          )}
        </div>

        {/* Map */}
        <div className="leaflet-map-container" style={{ marginTop: '16px' }}>
          <Map
            key={position.join(',')}
            marker={position}
            center={position}
            draggable={true}
            onChange={onMarkerMove}
          />
        </div>
      </div>
    </>
  );
};

export default LeafletWidget;
